/**
 * Shapes on the wire for organization export / archive-delete.
 *
 * A plain export IS the envelope. `archive-delete` returns the same envelope
 * nested: `{ message, archive: <envelope> }`. Both end up on disk as .json and
 * both are droppable into the import picker, so one loader handles both.
 */

/** The only export kind the import accepts. */
export const FULL_EXPORT_KIND = 'organization-full';

/** The export envelope, as built by `buildExportEnvelope` on the backend. */
export interface ExportEnvelope {
  exportVersion?: string;
  /**
   * `organization-full` = the complete, restorable shape. A tenant admin's own
   * download stamps `tenant-admin-scoped` and carries only their slice; the
   * import rejects it with a 400. Absent = a legacy file, still accepted.
   */
  exportKind?: string;
  exportDate?: string;
  /** Tables the source tenant's schema did not have — sections are ABSENT, not empty. */
  missingTables?: string[];
  /** Scoped exports only: tables deliberately left out of the slice. */
  omittedTables?: string[];
  organization?: { name?: string; slug?: string; [key: string]: unknown };
  [key: string]: unknown;
}

/** What the operator just picked, decided BEFORE they hit Validate. */
export interface ImportFileKind {
  /** False = the import would reject this file; say so now, not after a 400. */
  restorable: boolean;
  /** The stamped kind, or 'legacy' for a pre-`exportKind` file. */
  kind: string;
  /** Tables absent from the source schema. */
  missingTables: string[];
  /** Tables a scoped export deliberately left out. */
  omittedTables: string[];
}

const stringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

export const classifyImportFile = (envelope: ExportEnvelope | null): ImportFileKind | null => {
  if (!envelope) return null;
  const kind = typeof envelope.exportKind === 'string' ? envelope.exportKind : 'legacy';
  return {
    // Absent exportKind is a legacy full export and is still accepted.
    restorable: kind === 'legacy' || kind === FULL_EXPORT_KIND,
    kind,
    missingTables: stringList(envelope.missingTables),
    omittedTables: stringList(envelope.omittedTables),
  };
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Unwrap an archive-delete file down to the envelope; pass an export through. */
export const unwrapImportFile = (parsed: unknown): ExportEnvelope | null => {
  if (!isObject(parsed)) return null;
  if (!parsed.exportVersion && isObject(parsed.archive)) {
    return parsed.archive as ExportEnvelope;
  }
  return parsed as ExportEnvelope;
};

/**
 * Does this really look like a backup? The archive-delete data is already gone
 * by the time the browser sees it, so an unverified blob must never be offered
 * as one.
 */
export const isExportEnvelope = (value: unknown): value is ExportEnvelope =>
  isObject(value) &&
  typeof value.exportVersion === 'string' &&
  isObject(value.organization) &&
  typeof (value.organization as Record<string, unknown>).slug === 'string';
